import React from "react";
import { useDmsPreview } from "./DmsPreviewProvider";

/**
 * DmsPreviewContent — inner content of the DMS tab.
 *
 * Renders the iframe-based PDF viewer + a small toolbar at top.
 * Designed to be hosted INSIDE another container that handles
 * positioning, dimensions, and close-button (the AppShell's
 * <RightPanel>). This component just fills its parent.
 *
 * State comes from useDmsPreview(); no props needed.
 *
 * Empty state: if no doc has been loaded yet, shows a friendly
 * "no document selected" message — happens when operator clicks
 * the DMS icon in FloatingToolStrip without first triggering
 * dms.show() from a form.
 */
export default function DmsPreviewContent() {
  const dms = useDmsPreview();

  // ── Empty state — no document requested yet ──
  if (!dms.request && !dms.loading) {
    return (
      <div style={emptyStyle}>
        <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>📄</div>
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>No document selected</div>
        <div style={{ fontSize: "0.78rem", color: "#6b7280", textAlign: "center", maxWidth: 280 }}>
          Open a form (e.g. Gate Entry) and click <em>View Scan</em> to preview a document here.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Mini toolbar — title + reload */}
      <div style={toolbarStyle}>
        <div style={titleStyle} title={dms.filename || ""}>
          📄 <span style={ellipsisStyle}>{dms.filename || "Loading…"}</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {dms.request && (
            <button
              onClick={() => dms.show(dms.request)}    // re-fetch (new token)
              title="Reload"
              style={iconBtnStyle}
            >
              ↻
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={bodyStyle}>
        {dms.loading && !dms.url && (
          <div style={spinnerStyle}>Loading document…</div>
        )}
        {dms.error && (
          <div style={errorStyle}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Failed to load</div>
            <pre style={errorPreStyle}>{dms.error}</pre>
          </div>
        )}
        {dms.url && !dms.error && (
          <iframe
            src={dms.url}
            title={dms.filename || "DMS document"}
            style={iframeStyle}
          />
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  background: "#f3f4f6",
};

const toolbarStyle = {
  flex: "0 0 auto",
  padding: "6px 10px",
  background: "#1f2937",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.78rem",
  borderBottom: "1px solid #374151",
};

const titleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flex: 1,
  minWidth: 0,
};

const ellipsisStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const iconBtnStyle = {
  background: "transparent",
  border: "none",
  color: "white",
  cursor: "pointer",
  padding: "2px 6px",
  borderRadius: 3,
  fontSize: "0.85rem",
};

const bodyStyle = {
  flex: 1,
  position: "relative",
  background: "white",
  overflow: "hidden",
};

const iframeStyle = {
  width: "100%",
  height: "100%",
  border: "none",
};

const spinnerStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "#6b7280",
  fontSize: "0.85rem",
};

const errorStyle = {
  padding: "1rem",
  background: "#fef2f2",
  color: "#991b1b",
  fontSize: "0.8rem",
  height: "100%",
  overflow: "auto",
};

const errorPreStyle = {
  fontFamily: "ui-monospace, monospace",
  fontSize: "0.72rem",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
};

const emptyStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "#f9fafb",
  color: "#374151",
  padding: "1rem",
};
